"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const collections = [
{
  id: "c1",
  title: "Alphabet Adventures",
  desc: "26 letters, 26 magical creatures — trace, color, and learn the ABCs in the most delightful way.",
  price: "From $9.99",
  books: "12 Titles",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b8cab21-1767790341048.png",
  alt: "Alphabet adventure coloring books collection featuring illustrated letters with animals and characters",
  color: "#FFB5A7",
  accent: "#FF8E76",
  emoji: "🔤",
  span: "md:col-span-2 md:row-span-2",
  tall: true
},
{
  id: "c2",
  title: "Numbers & Counting",
  desc: "Fun counting games, number tracing, and colorful puzzles.",
  price: "From $8.99",
  books: "8 Titles",
  image: "https://images.unsplash.com/photo-1631847560046-32bc5ae736e5",
  alt: "Numbers and counting coloring workbooks for early childhood learning",
  color: "#A8DADC",
  accent: "#7EC8CB",
  emoji: "🔢",
  span: "md:col-span-2",
  tall: false
},
{
  id: "c3",
  title: "Magic & Fantasy",
  desc: "Unicorns, dragons, and enchanted forests await.",
  price: "From $12.99",
  books: "10 Titles",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11fef7273-1771028303034.png",
  alt: "Fantasy and magic coloring book collection with unicorns, fairies, and enchanted creatures",
  color: "#C8B6E2",
  accent: "#9B7DC8",
  emoji: "🦄",
  span: "md:col-span-2",
  tall: false
},
{
  id: "c4",
  title: "Accessories & Sets",
  desc: "Premium colored pencils, crayons, and watercolor sets to complete the creative experience.",
  price: "From $14.99",
  books: "15 Products",
  image: "https://images.unsplash.com/photo-1661732017114-6d554292c1b2",
  alt: "Art accessories set including colored pencils, crayons, and watercolor paints for children",
  color: "#F4C842",
  accent: "#E0B020",
  emoji: "🎨",
  span: "md:col-span-2",
  tall: false
}];


export default function CollectionsSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.remove("hidden-before-reveal");
        });
      },
      { threshold: 0.08 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => {
      el?.classList?.add("hidden-before-reveal");
      observer?.observe(el);
    });
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal text-center mb-14">
          <span className="text-xs font-black tracking-widest uppercase text-plum-light bg-plum-100 px-3 py-1 rounded-full">
            📚 Our Collections
          </span>
          <h2 className="font-fraunces text-4xl md:text-5xl font-black text-plum mt-4 leading-tight">
            A Book for Every
            <span className="italic text-plum-light"> Little Dream</span>
          </h2>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 auto-rows-[240px] md:auto-rows-[220px]">
          {collections?.map((col, i) =>
          <Link
            key={col?.id}
            href="/products"
            className={`reveal ${col?.span} group relative rounded-3xl overflow-hidden shadow-dreamy block`}
            style={{ transitionDelay: `${i * 0.12}s` }}>
            
              {/* Image */}
              <AppImage
              src={col?.image}
              alt={col?.alt}
              fill
              priority={i < 2}
              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 25vw, 33vw"
              className="object-cover transition-transform duration-700 group-hover:scale-108"
              style={{ transform: "scale(1)" }} />
            

              {/* Gradient overlay */}
              <div
              className="absolute inset-0 transition-opacity duration-500"
              style={{
                background: `linear-gradient(to top, ${col?.accent}ee 0%, ${col?.color}88 40%, transparent 70%)`
              }} />
            

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="text-2xl mb-1" aria-hidden="true">{col?.emoji}</div>
                <h3 className="font-fraunces font-black text-lg md:text-xl leading-tight">{col?.title}</h3>
                {col?.tall &&
              <p className="text-xs text-white/80 mt-1 leading-relaxed hidden md:block">{col?.desc}</p>
              }
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-bold text-white/80">{col?.books}</span>
                  <span className="text-sm font-black">{col?.price}</span>
                </div>
              </div>

              {/* Arrow on hover */}
              <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                <Icon name="ArrowRightIcon" size={16} className="text-white" />
              </div>
            </Link>
          )}
        </div>

        <div className="reveal text-center mt-10">
          <Link href="/products" className="btn-primary px-10 py-4 text-base inline-flex items-center gap-2">
            <Icon name="BookOpenIcon" size={18} variant="solid" />
            Browse All Collections
          </Link>
        </div>
      </div>
    </section>);

}